import React, { useEffect, useState, useRef } from 'react'
import {
  fetchCompanySetting,
  updateCompanySetting,
  fetchBrandingTemplates,    // ← Added: fetch list of saved templates
  fetchBrandingTemplate,     // ← Added: fetch a single template by ID
  saveBrandingTemplate       // ← Added: save a new branding template
} from '../utils/api'
import { applyCustomCss } from '../utils/branding'
import { downloadSelectorsTxt } from '../utils/selectors'
import Editor from '@monaco-editor/react'

export default function BrandingForm({ initialSettings = {}, onSave = () => {} }) {
  // ------------------------------------------------------------
  // Original state variables
  // ------------------------------------------------------------
  const [settings, setSettings] = useState(initialSettings)
  const [logoFile, setLogoFile] = useState(null)
  const [preview, setPreview] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const orig = useRef(initialSettings)

  // ------------------------------------------------------------
  // Added state variables for template management
  // ------------------------------------------------------------
  const [templates, setTemplates] = useState([])           // list of { id, name }
  const [selectedTemplateId, setSelectedTemplateId] = useState('new') // 'new' or template ID
  const [newTemplateName, setNewTemplateName] = useState('')         // name when creating/editing

  // ------------------------------------------------------------
  // Sync in initial settings
  // ------------------------------------------------------------
  useEffect(() => {
    const cfg = initialSettings || {}
    setSettings(cfg)
    orig.current = cfg
    applyCustomCss(cfg.custom_css || '')
    setPreview(cfg.logo_url || '')
    setLoading(false)
  }, [initialSettings])

  // If no initialSettings passed, fetch from server
  useEffect(() => {
    if (initialSettings.custom_css != null) return

    fetchCompanySetting()
      .then(data => {
        const cfg = data || {}
        orig.current = cfg
        setSettings(cfg)
        applyCustomCss(cfg.custom_css || '')
        setPreview(cfg.logo_url || '')
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [initialSettings])

  // ------------------------------------------------------------
  // Added: Load list of saved templates on mount
  // ------------------------------------------------------------
  useEffect(() => {
    fetchBrandingTemplates()
      .then(list => {
        setTemplates(list || [])
      })
      .catch(err => {
        console.error('Error fetching branding templates:', err)
      })
  }, [])

  // ------------------------------------------------------------
  // Logo preview (unchanged)
  // ------------------------------------------------------------
  const handleFileChange = e => {
    const file = e.target.files[0]
    if (!file) return
    setLogoFile(file)
    setPreview(URL.createObjectURL(file))
  }

  // ------------------------------------------------------------
  // Added: When a template is selected from dropdown
  // ------------------------------------------------------------
  const handleTemplateSelect = async e => {
    const tplId = e.target.value
    setSelectedTemplateId(tplId)

    if (tplId === 'new') {
      // Clear editor for a new template
      setSettings({ custom_css: '' })
      setLogoFile(null)
      setPreview(orig.current.logo_url || '')
      setNewTemplateName('')
      applyCustomCss('')
      return
    }

    // Otherwise, load that template's CSS/logo
    setLoading(true)
    setError('')
    try {
      const tpl = await fetchBrandingTemplate(tplId)
      // tpl = { id, name, custom_css, logo_url }
      setSettings({ custom_css: tpl.custom_css || '' })
      applyCustomCss(tpl.custom_css || '')
      setLogoFile(null)
      setPreview(tpl.logo_url || '')
      setNewTemplateName(tpl.name)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // ------------------------------------------------------------
  // Reset to index.css/Bootstrap baseline (unchanged)
  // ------------------------------------------------------------
  const handleReset = async () => {
    setSaving(true)
    setError('')
    try {
      const form = new FormData()
      form.append('company_setting[remove_logo]', 'true')
      form.append('company_setting[custom_css]', '')
      
      const result = await updateCompanySetting(form)
      setSettings(result)
      applyCustomCss('')
      setLogoFile(null)
      setPreview(result.logo_url || '')
      onSave(result)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // ------------------------------------------------------------
  // Save custom CSS (and logo) OR save as a new template
  // ------------------------------------------------------------
  const handleSubmit = async e => {
    e.preventDefault()
    setSaving(true)
    setError('')

    // If the admin has selected an existing template or is creating a new one,
    // we branch: saving a template vs. updating the company setting directly.
    // We treat every "Save Branding" click as "save a template".
    //
    // 1) Require a template name
    if (!newTemplateName.trim()) {
      setError('Please enter a Template Name before saving.')
      setSaving(false)
      return
    }

    try {
      // Build FormData for saving a new template (or overwriting existing)
      const f = new FormData()
      f.append('template[name]', newTemplateName.trim())
      f.append('template[custom_css]', settings.custom_css || '')
      if (logoFile) {
        f.append('template[logo_file]', logoFile)
      }

      // If editing an existing template, append its ID so the backend can update
      if (selectedTemplateId !== 'new') {
        f.append('template[id]', selectedTemplateId)
      }

      const createdOrUpdated = await saveBrandingTemplate(f)
      // createdOrUpdated = { id, name, custom_css, logo_url }

      // Apply it live (update settings & CSS)
      setSettings({ custom_css: createdOrUpdated.custom_css })
      applyCustomCss(createdOrUpdated.custom_css)
      setLogoFile(null)
      setPreview(createdOrUpdated.logo_url || '')

      // Refresh the dropdown list of templates
      const refreshedList = await fetchBrandingTemplates()
      setTemplates(refreshedList || [])
      setSelectedTemplateId(createdOrUpdated.id)
      setNewTemplateName(createdOrUpdated.name)

      // Immediately write this template’s CSS/logo into CompanySetting
      // so that it “sticks” across logout/login.
      const csForm = new FormData()

      // Add CSS
      csForm.append('company_setting[custom_css]', createdOrUpdated.custom_css || '')
      
      // Add logo: either a fresh file or reuse template’s URL
      if (logoFile) {
        csForm.append('company_setting[logo_file]', logoFile)
      } else if (createdOrUpdated.logo_url) {
        csForm.append('company_setting[logo_url]', createdOrUpdated.logo_url) // fallback
      }
      
      await updateCompanySetting(csForm)      

      onSave(createdOrUpdated)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <p>Loading…</p>

  return (
    <form onSubmit={handleSubmit} className="branding-form space-y-6">
      {error && <div className="branding-form_error alert alert-danger">{error}</div>}

      {/* ------------------------------------------------------------ */}
      {/* Added: Template Dropdown */}
      {/* ------------------------------------------------------------ */}
      <div>
        <label className="branding-form_section branding-form_template form-label">Template</label>
        <select
          className="branding-form_template-select form-select"
          value={selectedTemplateId}
          onChange={handleTemplateSelect}
          disabled={saving}
        >
          <option value="new">→ New Template…</option>
          {templates.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {tpl.name}
            </option>
          ))}
        </select>
      </div>

      {/* ------------------------------------------------------------ */}
      {/* Added: Template Name Input */}
      {/* ------------------------------------------------------------ */}
      <div>
        <label className="branding-form_section branding-form_template-name form-label">Template Name</label>
        <input
          type="text"
          className="branding-form_template-name-input form-control"
          placeholder="e.g. “Spring Campaign”"
          value={newTemplateName}
          onChange={(e) => setNewTemplateName(e.target.value)}
          disabled={saving}
        />
        <small className="form-text text-muted">
          Give this design a name so you can select it later.
        </small>
      </div>

      {/* ------------------------------------------------------------ */}
      {/* Logo Upload (original) */}
      {/* ------------------------------------------------------------ */}
      <div>
        <label className="branding-form_section branding-form_logo-upload form-label">Logo Upload</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="branding-form_logo-upload-input form-control"
          disabled={saving}
        />
        {preview && (
          <div className="branding-form_logo-preview mt-2">
            <img src={preview} alt="logo preview" className="branding-form_logo-preview-img" style={{ maxHeight: 80 }} />
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------ */}
      {/* Custom CSS Editor (original) */}
      {/* ------------------------------------------------------------ */}
      <div>
        <label className="branding-form_section branding-form_css-editor form-label mt-2">Custom CSS</label>
        <Editor
          height="300px"
          defaultLanguage="css"
          value={settings.custom_css || ''}
          onChange={(code) => {
            const css = code ?? ''
            setSettings((prev) => ({ ...prev, custom_css: css }))
            applyCustomCss(css)
          }}
          theme="vs-dark"
          options={{ wordWrap: 'on', minimap: { enabled: false }, fontSize: 14 }}
        />
      </div>

      {/* ------------------------------------------------------------ */}
      {/* Download selectors template (original) */}
      {/* ------------------------------------------------------------ */}
      <p className="branding-form_section branding-form_selectors-download mb-3 my-4">
        <button
          type="button"
          className="branding-form_selectors-download-btn btn btn-outline-secondary btn-sm"
          onClick={downloadSelectorsTxt}
          disabled={saving}
        >
          📥 Download selectors template
        </button>
      </p>

      {/* ------------------------------------------------------------ */}
      {/* Actions: Save Branding & Reset (original) */}
      {/* ------------------------------------------------------------ */}
      <div className="branding-form_section branding-form_actions d-flex gap-3">
        <button
          type="submit"
          className="branding-form_save-btn btn btn-primary flex-grow-1"
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save Branding'}
        </button>

        <button
          type="button"
          onClick={handleReset}
          className="branding-form_reset-btn btn btn-secondary flex-grow-1"
          disabled={saving}
        >
          Reset to Default
        </button>
      </div>
    </form>
  )
}
